// Presentation layer assembly for TaskItemBase controller.
import { useMemo } from 'react';
import { formatTaskDate } from '@/features/tasks/utils/views/taskTimeUtils';
import { normalizeTaskTags } from '@/features/tasks/utils/data/taskTags';
import { useTaskItemStyles } from '@/features/tasks/hooks/ui/use-task-item-styles.js';
import { useTaskHierarchy } from '@/features/tasks/hooks/ui/use-task-hierarchy.js';
import {
  buildTaskItemContentProps,
  buildTaskItemViewFlags,
  buildBreadcrumbTooltip,
  buildTaskItemPresentation,
  buildTaskItemMetadataSection,
  resolveShowAddSubtaskButton,
} from './useTaskItemBaseController.helpers';
import { getPartiallyScheduledProgress } from '@/features/tasks/utils/views/filtersViewUtils';

export function useTaskItemBasePresentation({
  task,
  isOverlay,
  variant,
  breadcrumb,
  index,
  showPlayButton,
  finalUser,
  finalTasks,
  finalProjectDisplayMap,
  finalProjectColorMap,
  finalContextDisplayMap,
  finalActiveFocusTaskId,
  finalFocusStartTime,
  finalStopFocus,
  finalHandleStartFocus,
  finalHandleToggleComplete,
  finalHandleDeleteTask,
  view,
  indentationType,
  onStartAddingSubtask,
  onRebalance,
  duplicateOrderKeyTaskIds,
  isExecutionView,
  breadcrumbArray,
  displayLevel,
  menuSurface,
  addSubtaskOnParentsOnly,
  isSmallScreen,
  listId,
  focusOwnerId,
  selectedTaskIds,
  isMultiSelectActive,
  onToggleInSelection,
  localCompleted,
  setLocalCompleted,
  isDragDisabled,
  transform,
  transition,
  isDragging,
  isOver,
  isTouchDevice,
  showRowDelete,
  displayDuration,
  isRollup,
  tasksMap,
}) {
  const viewFlags = buildTaskItemViewFlags(view, menuSurface);
  const { allProjects, allContexts } = useMemo(
    () => normalizeTaskTags(task),
    [task],
  );
  const formattedDate = useMemo(() => formatTaskDate(task?.date), [task?.date]);
  const hasConflict = duplicateOrderKeyTaskIds?.has(task?.id);
  const isFocused = finalActiveFocusTaskId === task?.id;

  const breadcrumbTooltip = buildBreadcrumbTooltip({
    isExecutionView,
    isPlanView: viewFlags.isPlanView,
    isTouchDevice,
    breadcrumbArray,
  });

  const { isFirstSubtask, isLastSubtask, parentContextColor } =
    useTaskHierarchy({
      index,
      tasks: finalTasks,
      displayLevel,
      indentationType,
      isExecutionView,
      view,
      listId: listId ?? view,
    });

  const { style, classNames: baseClassNames } = useTaskItemStyles({
    task,
    allContexts,
    isOverlay,
    isSmallScreen,
    isDragDisabled,
    transform,
    transition,
    isDragging,
    isOver,
    variant,
    localCompleted,
    isFocused,
  });

  const { classNames: presentationClassNames, finalStyle } =
    buildTaskItemPresentation({
      style,
      baseClassNames,
      isFirstSubtask,
      isLastSubtask,
      parentContextColor,
    });

  const isPartiallyScheduled =
    listId === 'unscheduled' &&
    Boolean(getPartiallyScheduledProgress(task, tasksMap));
  const classNames = isPartiallyScheduled
    ? `${presentationClassNames} is-partially-scheduled`
    : presentationClassNames;

  const metadataSection = buildTaskItemMetadataSection({
    isSmallScreen,
    allProjects,
    projectDisplayMap: finalProjectDisplayMap,
    projectColorMap: finalProjectColorMap,
    allContexts,
    contextDisplayMap: finalContextDisplayMap,
    hasConflict,
    onRebalance,
    task,
    formattedDate,
    viewFlags,
    isExecutionView,
    displayDuration,
    isRollup,
    isFocused,
    focusStartTime: finalFocusStartTime,
    localCompleted,
    listId,
    tasksMap,
  });

  const showAddSubtaskButton = resolveShowAddSubtaskButton(task, {
    view,
    addSubtaskOnParentsOnly,
    isSmallScreen,
    isExecutionView,
  });

  const contentProps = buildTaskItemContentProps({
    task,
    localCompleted,
    setLocalCompleted,
    finalUser,
    finalHandleToggleComplete,
    finalActiveFocusTaskId,
    finalFocusStartTime,
    finalStopFocus,
    isSmallScreen,
    isExecutionView,
    isTouchDevice,
    breadcrumb,
    isFocused,
    displayDuration,
    showPlayButton,
    finalHandleStartFocus,
    metadataSection,
    onStartAddingSubtask,
    finalHandleDeleteTask,
    view,
    showAddSubtaskButton,
    showRowDelete,
    selectedTaskIds,
    listId,
    focusOwnerId,
    isMultiSelectActive,
    onToggleInSelection,
  });

  return {
    displayDuration,
    classNames,
    finalStyle,
    breadcrumbTooltip,
    contentProps,
  };
}
